import { useEffect, useRef, useState } from 'react'
import FileUpload from '../FileUpload/FileUpload';
import { IFile, ITemporaryFile } from '../../helpers/Interfaces';
import { convertDuration, convertTo2DecimalPlaces, formatMB, getDateFromTimeStamp, getFileSize } from '../../helpers/HelperFunctions';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { storage } from '../../services/FirebaseService';
import Trash from "../../assets/images/Trash2.svg";
import File from "../../assets/images/File.svg";
import Close from "../../assets/images/Close.svg";
import Download from "../../assets/images/Download.svg";
import { downloadMedia } from '../../helpers/FirebaseFunctions';
import { Popover } from 'antd';
import BlueHomeDot from "../../assets/images/BlueHomeDot.svg";
import BlueHomeDotDisable from "../../assets/images/BlueHomeDotDisable.svg";
import { Colors } from "../../helpers/Colors";
import { Checkbox } from "@mui/material";

import "./FileUploadUI.scss"

type FileUploadUIProps = {
    setLoading: CallableFunction;
    setMedia: CallableFunction;
    media: IFile[];
    setShowAlert: CallableFunction;
    acceptedFileTypes?: [".mp3", ".m4a", ".wav", ".ds2", ".amr"] | [".doc", ".docx", ".pdf"] | [".png", ".jpeg", ".jpg"]
    | [".xls", ".xlsx", ".csv"] | [".mp3", ".m4a", ".wav", ".ds2", ".amr", ".doc", ".docx", ".pdf", ".png", ".jpeg", ".jpg", ".xls", ".xlsx", ".csv"];
    removeFile: CallableFunction;
    showDelete?: boolean;
    showMultiSelectDelete?: boolean;
    removeMulitpleFiles: CallableFunction;
}

function FileUploadUI(props: Readonly<FileUploadUIProps>) {
    const [temporaryFiles, setTemporaryFiles] = useState([] as ITemporaryFile[]);
    const [progress, setProgress] = useState(0);
    const [deleteMediaId, setDeleteMediaId] = useState<string | null>(null);
    const [selectedMedia, setSelectedMedia] = useState<string[]>([]);
    const [showMultipleDeleteMsg, setShowMultipleDeleteMsg] = useState(false);

    const uploadRef = useRef({} as any);

    useEffect(() => {
        if (temporaryFiles.length > 0) {
            uploadTemporaryFile(0)
        }
    }, [temporaryFiles, temporaryFiles.length]);

    function uploadTemporaryFile(index: number) {

        const storageRef = ref(storage, `/media/${temporaryFiles[index].fileId}`);

        let _temporaryFiles = temporaryFiles;
        _temporaryFiles[index].state = "Uploading";
        setTemporaryFiles(_temporaryFiles);

        uploadRef.current = uploadBytesResumable(storageRef, temporaryFiles[index]?.file);

        uploadRef.current.on('state_changed',
            (snapshot: any) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setProgress(progress)
            },
            (error: any) => {
                switch (error.code) {
                    case 'storage/canceled':
                        let _temporaryFiles = temporaryFiles;
                        _temporaryFiles[index].state = "Cancelled";
                        setTemporaryFiles(_temporaryFiles);

                        if ((index + 1) < temporaryFiles.length) {
                            uploadTemporaryFile(index + 1)
                        } else {
                            setTemporaryFiles([] as ITemporaryFile[]);
                        }

                        props.setLoading(false);
                }
            },
            () => {
                getDownloadURL(uploadRef?.current?.snapshot.ref).then((downloadURL) => {

                    let _temporaryFiles = temporaryFiles;

                    _temporaryFiles[index].link = downloadURL;
                    _temporaryFiles[index].state = "Uploaded";

                    setTemporaryFiles(_temporaryFiles);

                    let mediaFile: IFile = {
                        fileUrl: downloadURL,
                        fileId: _temporaryFiles[index].fileId,
                        fileName: _temporaryFiles[index].fileName,
                        fileExtension: _temporaryFiles[index]?.file?.type,
                        fileSize: getFileSize(_temporaryFiles[index]?.file.size.toString()),
                        duration: _temporaryFiles[index]?.fileName?.split('.').pop() === 'ds2'
                            ? convertTo2DecimalPlaces(0.20)
                            : convertTo2DecimalPlaces(_temporaryFiles[index]?.duration),
                        saved: false
                    };

                    props.setMedia(mediaFile);

                    if ((index + 1) < temporaryFiles.length) {
                        uploadTemporaryFile(index + 1)
                    } else {
                        setTemporaryFiles([] as ITemporaryFile[]);
                    }

                    props.setLoading(false);
                });
            }
        );
    }

    function isFileExist(value: { url: string; base64Url: any; file: any; duration: number; }) {
        return props.media.some((file: any) => {
            return file?.fileName === (value?.file?.name);
        });
    }

    function getHeader() {
        let temporaryPendingFiles = temporaryFiles.filter((file) => file.state === 'Pending').length;
        let uploadedFiles = props.media.length;
        let uploadingFiles = temporaryFiles.filter((file) => file.state === 'Uploading').length;

        let message = 'Please upload your files here..';

        if (uploadedFiles > 0) {
            message = uploadedFiles === 1 ? '1 item uploaded' : uploadedFiles + ' items uploaded';
        }

        if (uploadedFiles > 0 && uploadingFiles > 0) {
            message = message + ', ' + (uploadingFiles === 1 ? '1 item uploading' : uploadingFiles + ' items uploading')
        } else if (uploadingFiles > 0) {
            message = uploadingFiles === 1 ? '1 item uploading' : uploadingFiles + ' items uploading'
        }

        if (uploadedFiles > 0 && uploadingFiles > 0 && temporaryPendingFiles > 0) {
            message = message + ', ' + (temporaryPendingFiles === 1 ? '1 item pending' : temporaryPendingFiles + ' items pending')
        } else if (temporaryPendingFiles > 0) {
            message = temporaryPendingFiles === 1 ? '1 item pending' : temporaryPendingFiles + ' items pending'
        }

        return message;
    }

    const renderPreviewContent = (file: IFile) => {
        const fileName = file.fileName.toLowerCase();
        const isPDF = fileName.endsWith('.pdf');
        const isDoc = fileName.endsWith('.doc') || fileName.endsWith('.docx');

        if (!isPDF && !isDoc) return null;

        const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(file.fileUrl)}&embedded=true`;

        return (
            <div className="file-hover-preview">
                <iframe src={isPDF ? file.fileUrl : viewerUrl} title="File Preview" width="400px" height="500px" />
            </div>
        );
    };

    const renderImageContent = (file: IFile) => {
        const fileName = file.fileName.toLowerCase();

        const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/.test(fileName);

        if (!isImage) return null;

        return (
            <div className="file-hover-preview">
                <img src={file.fileUrl} alt={file.fileName} style={{ width: '43rem', height: '31rem' }} />
            </div>
        );
    };

    return (
        <div className='file-upload-ui'>
            <div className="file-uploader">
                <FileUpload heading={getHeader()} label={"Import File"} showUploadedImage={false} multipleFiles
                    subHeading={"You can “drag & drop files” here or click to below link"}
                    acceptedFileTypes={props.acceptedFileTypes || [".mp3", ".m4a", ".wav", ".ds2", ".amr", ".doc", ".docx", ".pdf", ".png", ".jpeg", ".jpg", ".xls", ".xlsx", ".csv"]}
                    onChange={(values: { url: string; base64Url: any; file: any; duration: number; }[]) => {
                        if (temporaryFiles.length > 0) {
                            let newShowAlert = { isError: true, message: 'Please Wait. Upload is Currently in Progress', open: true };
                            props.setShowAlert(newShowAlert);
                            return;
                        }

                        props.setLoading(true);

                        const newTemporaryFiles: ITemporaryFile[] = [];

                        values?.forEach((value) => {
                            (async () => {
                                let fileAlreadyExists = isFileExist(value)

                                if (fileAlreadyExists) {
                                    let newShowAlert = { isError: true, message: `${value.file?.name} Already Exists`, open: true };
                                    props.setShowAlert(newShowAlert);
                                    props.setLoading(false);
                                } else {
                                    let dateTime = new Date().getTime().toString();
                                    let fileName = dateTime + "_" + value.file?.name

                                    newTemporaryFiles.push({
                                        file: value?.file, duration: value.duration, link: "",
                                        fileName: value.file?.name, fileId: fileName, state: "Pending"
                                    });
                                }
                            })()
                        });

                        if (newTemporaryFiles.length > 0) {
                            setTemporaryFiles(newTemporaryFiles);
                        } else {
                            props.setLoading(false);
                        }
                    }}
                />
            </div>
            {selectedMedia.length > 0
                ? (<>
                    {showMultipleDeleteMsg
                        ? (<div className='mulitple-delete-confirmation'>
                            <span>{`Delete ${selectedMedia.length} File/s?`}</span>
                            <div className='delete-buttons'>
                                <span className='delete-button' onClick={() => {
                                    props.removeMulitpleFiles(selectedMedia);
                                    setShowMultipleDeleteMsg(false);
                                    setSelectedMedia([]);
                                }}>
                                    Yes
                                </span>
                                <span className='delete-button' onClick={() => {
                                    setShowMultipleDeleteMsg(false);
                                    setSelectedMedia([]);
                                }}>
                                    No
                                </span>
                            </div>
                        </div>)
                        : (<div className='multi-select-area'>
                            <div className='delete-all-wrapper' onClick={() => { setShowMultipleDeleteMsg(true); }}>
                                Delete All
                            </div>
                        </div>)
                    }
                </>)
                : (<></>)
            }
            <div className="uploaded-files-area">
                <div className="file-doc">
                    {temporaryFiles.map((_file: ITemporaryFile) => {
                        if (_file.state === "Pending") {
                            return (
                                <div key={_file.fileId} className="file-content-view ">
                                    <div className="file-data">
                                        <img src={File} alt="" />
                                        <label className="file-name">{_file.fileName}</label>
                                    </div>
                                    <div className="file-properties">
                                        <span className="size pending">Pending</span>
                                        <span className="size">{getFileSize(_file.file?.size.toString()) + " MB"}</span>
                                        <div className='img-content'>
                                        </div>
                                        <div className='img-content'>
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                    })}
                    {temporaryFiles.map((_file: ITemporaryFile) => {
                        if (_file.state === "Uploading") {
                            return (
                                <div key={_file.fileId} className="file-content-view ">
                                    <div className="file-data">
                                        <img src={File} alt="" />
                                        <label className="file-name">{_file.fileName}</label>
                                    </div>
                                    <div className="file-properties">
                                        <span className="size uploading">{progress?.toFixed(0)} %</span>
                                        <span className="size">{getFileSize(_file.file?.size.toString()) + " MB"}</span>
                                        <div className='img-content'>
                                        </div>
                                        <div className='img-content'>
                                            {progress < 100
                                                ? (<img onClick={() => { uploadRef.current.cancel(); props.setLoading(false); }}
                                                    src={Close} alt="Trash" />)
                                                : (<></>)
                                            }
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                    })}
                    {props.media.map((file: IFile) => {
                        return file?.fileExtension?.includes("image")
                            ? (<></>)
                            : (<div key={file.fileId} className={`${(deleteMediaId && deleteMediaId === file.fileId) ? "file-content-view delete" : "file-content-view"}`}>
                                <div className="file-data">
                                    {props?.showMultiSelectDelete
                                        ? (<Checkbox icon={<img alt='check box' src={BlueHomeDotDisable} />}
                                            checkedIcon={<img alt='check' src={BlueHomeDot} />}
                                            value="cryon" style={{ color: Colors.white_10 }}
                                            checked={selectedMedia.includes(file.fileId)}
                                            onClick={(e) => {
                                                setSelectedMedia((prev) => prev.includes(file.fileId)
                                                    ? prev.filter((item) => item !== file.fileId)
                                                    : [...prev, file.fileId]
                                                );
                                                e.stopPropagation();
                                            }} />)
                                        : (<></>)
                                    }
                                    <img src={File} alt="File" />
                                    <Popover placement="top" title={file.fileName} trigger="hover" mouseEnterDelay={0.3}
                                        content={
                                            <div className="file-hover-preview">
                                                {renderPreviewContent(file)}
                                            </div>
                                        }
                                        overlayStyle={{ zIndex: 1500 }}
                                    >
                                        <label style={{ cursor: 'pointer', position: 'relative' }} className="file-name">
                                            {
                                                (deleteMediaId && deleteMediaId === file.fileId)
                                                    ? "Delete " + file?.fileName + "?"
                                                    : file?.fileName
                                            }
                                        </label>
                                    </Popover>
                                </div>
                                {(deleteMediaId && deleteMediaId === file.fileId)
                                    ? <div className='delete-buttons'>
                                        <span className='delete-button' onClick={() => { props.removeFile(deleteMediaId) }}>Yes</span>
                                        <span className='delete-button' onClick={() => { setDeleteMediaId(null) }}>No</span>
                                    </div>
                                    : <div className="file-properties">
                                        {file?.duration !== 0
                                            ? <span className="duration">{convertDuration(file?.duration)}</span>
                                            : <></>
                                        }
                                        <span className="size">{formatMB(file?.fileSize)}</span>
                                        <span className="size">{getDateFromTimeStamp(file?.fileId)}</span>
                                        <div className='img-content'>
                                            {file.saved
                                                ? (<img onClick={() => downloadMedia({ fileName: file.fileId, downloadingName: file.fileName })} src={Download} alt="Download" />)
                                                : (<></>)
                                            }
                                        </div>
                                        {props.showDelete !== false
                                            ? (<div className='img-content'><img onClick={() => {
                                                if (temporaryFiles.length > 0) {
                                                    let newShowAlert = { isError: true, message: 'Please Wait. Upload is Currently in Progress', open: true };
                                                    props.setShowAlert(newShowAlert);
                                                    return;
                                                }
                                                setDeleteMediaId(file.fileId);
                                            }} src={Trash} alt="Trash" />
                                            </div>)
                                            : (<></>)
                                        }
                                    </div>
                                }
                            </div>)
                    })}
                </div>
                <div className="file-img">
                    {props.media.map((file: IFile) => {
                        return file?.fileExtension?.includes("image")
                            ? (<div key={file.fileId} className="img-uploaded">
                                <Popover placement="top" title={file.fileName} trigger="hover" mouseEnterDelay={0.3}
                                    content={
                                        <div className="file-hover-preview">
                                            {renderImageContent(file)}
                                        </div>
                                    }
                                    overlayStyle={{ zIndex: 1500 }}
                                >
                                    <img className='image-source' src={file?.fileUrl} alt="File" />
                                </Popover>
                                {props.showDelete !== false
                                    ? (<span className="delete-content">
                                        <img onClick={() => {
                                            if (temporaryFiles.length > 0) {
                                                let newShowAlert = { isError: true, message: 'Please Wait. Upload is Currently in Progress', open: true };
                                                props.setShowAlert(newShowAlert);
                                                return;
                                            }
                                            props.removeFile(file)
                                        }} src={Close} alt="Close" />
                                    </span>)
                                    : (<></>)
                                }
                                {file.saved
                                    ? (<span className="download-content">
                                        <img onClick={() => downloadMedia({ fileName: file.fileId, downloadingName: file.fileName })} src={Download} alt="Close" />
                                    </span>)
                                    : (<></>)
                                }
                            </div>)
                            : (<></>)
                    })}
                </div>
            </div>
        </div>
    )
}

export default FileUploadUI